<template>
    <div>
        <h2>
            Loss of Income to Date of Trial
        </h2>

        <div class="a-form-inputs--error" v-if="pastLossTotal == 0">
            Claimant has no past loss of income.
        </div>
        <div class="a-table a-table--short-no-style a-table--full-width">
            <div :class="{ 'a-loader--table' : isLoading || pastLossPeriods == null }"></div>
            <table cellspacing="0" cellpadding="0" class="a-table--full-width" v-if="!isLoading && pastLossTotal">
                <thead>
                    <tr>
                        <th colspan="2">
                            Dates (from&ndash;to)
                        </th>
                        <th>
                            Duration (years)
                        </th>
                        <th>
                            Income Multiplicand
                        </th>
                        <th>
                            Discounted Multiplicand
                        </th>
                        <th>
                            Multiplier
                        </th>
                        <th>
                            Total Loss of Income
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="period in pastLossPeriods">
                        <td>
                            {{ period.period_from }}
                        </td>
                        <td>
                            {{ period.period_to }}
                        </td>
                        <td>
                            {{ roundValue(period.duration_years, 2) }}
                        </td>
                        <td>
                            {{ currencyFormat(period.income_multiplicand) }}
                        </td>
                        <td>
                            {{ currencyFormat(period.discounted_multiplicand) }}
                        </td>
                        <td>
                            {{ roundValue(period.multiplier, 2) }}
                        </td>
                        <td>
                            {{ currencyFormat(period.value) }}
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr class="a-table--grey">
                        <td class="u-text-right" colspan="6">
                            Total Loss of Income to Date of Trial:
                        </td>
                        <td>
                            <strong>
                                {{ currencyFormat(pastLossTotal) }}
                            </strong>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>

        <br>

        <h2>
            Loss of Income and Pension Post-Trial &amp; Lost Years
        </h2>

        <div class="a-form-inputs--error" v-if="futureLossTotal == 0">
            Claimant has no loss of income post-trial or loss of income and/or pension in lost years.
        </div>
        <div class="a-table a-table--short-no-style a-table--full-width">
            <div :class="{ 'a-loader--table' : isLoading || futureLossPeriods == null }"></div>
            <table cellspacing="0" cellpadding="0" class="a-table--full-width" v-if="!isLoading && futureLossTotal">
                <thead>
                    <tr>
                        <th colspan="2">
                            Dates (from&ndash;to)
                        </th>
                        <th>
                            Duration (years)
                        </th>
                        <th>
                            Deferment (years)
                        </th>
                        <th>
                            Income Multiplicand
                        </th>
                        <th>
                            Discounted Multiplicand
                        </th>
                        <th>
                            Multiplier
                        </th>
                        <th v-if="claimant.income.contingencyMultiplier">
                            Employment Contingency Multiplier
                        </th>
                        <th>
                            Total Loss of Income
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(period, index) in futureLossPeriods">
                        <td>
                            {{ period.period_from }}
                        </td>
                        <td>
                            {{ period.period_to }}
                        </td>
                        <td>
                            {{ roundValue(period.duration_years, 2) }}
                        </td>
                        <td>
                            {{ roundValue(period.deferment, 2) }}
                        </td>
                        <td>
                            {{ currencyFormat(period.income_multiplicand) }}
                        </td>
                        <td>
                            {{ currencyFormat(period.discounted_multiplicand) }}
                        </td>
                        <td>
                            {{ roundValue(period.multiplier, 2) }}
                        </td>
                        <td v-if="claimant.income.contingencyMultiplier">{{ roundValue(claimant.income.multiplier,2) }}</td>
                        <td>
                            <tr>
                                <td style="border: none; padding: 0px;">
                                    {{ currencyFormat(period.value) }}
                                </td>
                                <td style="border: none; padding: 0 0 0 10px">
                                    <div @click="toggleModal(period)">
                                        <icon-tooltip
                                                :tooltip-name="'calculation_' + uniqueId"
                                                tooltip-text="Click to view calculation"
                                                side="left"
                                                text-width="140"
                                                icon-type="formula"
                                        ></icon-tooltip>
                                    </div>
                                </td>
                            </tr>
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr class="a-table--grey">
                        <td class="u-text-right" :colspan="claimant.income.contingencyMultiplier ? 8 : 7">
                            Total Loss of Income from Date of Trial to end of 'But For' Life Expectancy:
                        </td>
                        <td>
                            <strong>
                                {{ currencyFormat(futureLossTotal) }}
                            </strong>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>

        <br><br>

        <step-button :next-valid="true"></step-button>

        <modal :name="'calculation_' + uniqueId" no-default-button="true">
            <modal-content></modal-content>
        </modal>
    </div>
</template>
<script>
    import Vue from 'vue'
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import StepButton from 'components/StepButton.vue';
    import IconTooltip from 'components/IconTooltip';
    import Modal from 'components/modal/Modal.vue';
    import ModalContent from 'components/modal/ModalContent.vue';
    import ModalInTheWild from 'components/modal/ModalInTheWild.vue';
    import currencyFormat from 'mixins/currencyFormat';
    import roundValue from 'mixins/roundValue';
    import {mapActions, mapGetters} from 'vuex';
    import { setStepValid }  from 'services/routingMethods';

    export default {
        name: 'loss-of-income',
        mounted() {
            this.refresh();

            setStepValid(true);
        },
        mixins: [
            currencyFormat,
            roundValue,
        ],
        computed: {
            ...mapGetters('lost/claimant', ['claimant', 'getClaimantInfoValue']),
            ...mapGetters('lost/lossOfIncome', [
                'pastLossPeriods',
                'pastLossTotal',
                'futureLossPeriods',
                'futureLossTotal',
                'isLoading'
            ]),
            ...mapGetters('http', ['apiRouteRequestRunning']),
            uniqueId() {
                return this._uid;
            }
        },
        components: {
            StepButton,
            Control,
            ControlError,
            IconTooltip,
            Modal,
            ModalContent,
            ModalInTheWild
        },
        methods: {
            ...mapActions('lost/lossOfIncome', ['refresh']),
            ...mapActions('modal', ['toggle', 'getModalContent']),
            toggleModal(period) {
                // Open the Modal Up
                this.toggle('calculation_' + this.uniqueId);

                this.getModalContent({
                    endpoint: 'postAssessmentMultiplier',
                    payload: {period: period, discountRate: this.getClaimantInfoValue('discountRate')}
                });
            },
        }
    }
</script>



// WEBPACK FOOTER //
// LossOfIncome.vue?1ff8504c